import { useState } from "react";
import { FileStack, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api, fmtVal, runExtract } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { SourceBadge } from "@/components/meridian/SourceBadge";
import { ProjectionChart } from "./ProjectionChart";

function MetricCard({ m, metrics, big }) {
  const { openSource } = useApp();
  const p = m.policy;
  return (
    <button data-testid={`metric-card-${m.key}`} onClick={() => openSource({ metric: m, metrics })}
      className="rise panel p-4 text-left hover:border-sky-500/50 hover:bg-slate-50 transition-colors group">
      <div className="flex justify-between items-start">
        <span className="eyebrow">{m.label}</span>
        {p && <span className={`text-[10px] num px-1.5 py-0.5 rounded border ${p.pass ? "border-emerald-500/40 text-emerald-700 bg-emerald-500/5" : "border-red-500/40 text-red-700 bg-red-500/10"}`}>{p.pass ? "PASS" : "BREACH"} {p.op} {fmtVal(p.threshold, m.format)}</span>}
      </div>
      <div className={`num ${big ? "text-3xl" : "text-xl"} text-slate-900 mt-2`}>{fmtVal(m.value, m.format, m.format === "usd")}</div>
      <div className="text-[10px] num text-slate-400 mt-2 group-hover:text-sky-600 transition-colors truncate">{m.missing.length ? `missing: ${m.missing.join(", ")}` : `${m.formula} · trace`}</div>
    </button>
  );
}

function EmptyState({ task, reload, setTab }) {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      if (!task.documents.length) await api.post(`/tasks/${task.id}/documents/samples`);
      await runExtract(task.id);
      await reload();
      toast.success("Facts extracted and ratios calculated");
    } catch (e) { if (!String(e?.response?.status).startsWith("403")) toast.error("Extraction failed - retry"); }
    setBusy(false);
  };
  return (
    <div className="m-6 panel p-10 grid-bg" data-testid="loan-empty-state">
      <FileStack className="w-6 h-6 text-sky-600" />
      <h2 className="font-display text-xl font-bold text-slate-900 mt-4">No facts extracted yet</h2>
      <p className="text-sm text-slate-600 mt-1 max-w-md">{task.documents.length ? `${task.documents.length} documents attached.` : "Attach the loan document set (rent roll, appraisal, financials, term sheet) or upload your own from the Evidence tab."} OmniFi will extract facts with page citations, then fixed calculators compute the ratios.</p>
      <button data-testid="extract-analyze-button" disabled={busy} onClick={task.custom && !task.documents.length ? () => setTab("evidence") : run} className="mt-6 inline-flex items-center gap-2 bg-sky-600 hover:bg-sky-700 text-white text-sm font-medium px-4 py-2 rounded transition-colors disabled:opacity-50">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
        {busy ? "Extracting with AI…" : task.documents.length ? "Extract & analyze" : task.custom ? "Upload documents" : "Attach samples & analyze"}
      </button>
    </div>
  );
}

export function LoanDashboard({ task, reload, setTab }) {
  if (!task.facts.length) return <EmptyState task={task} reload={reload} setTab={setTab} />;
  const M = task.metrics;
  const keyFacts = task.facts.filter((f) => ["gross_potential_rent", "occupancy_rate", "operating_expenses", "appraised_value", "loan_amount", "interest_rate", "amortization_years", "top_tenant"].includes(f.key));
  return (
    <div className="p-6 space-y-6" data-testid="loan-dashboard">
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
        {["noi", "ltv", "dscr", "debt_yield"].map((k) => <MetricCard key={k} m={M[k]} metrics={M} big />)}
      </div>
      <div className="grid xl:grid-cols-3 gap-4">
        <div className="xl:col-span-2 min-w-0"><ProjectionChart projection={task.projection} /></div>
        <div className="panel">
          <div className="px-4 py-3 border-b border-slate-200 flex justify-between"><span className="eyebrow">Key extracted facts</span><span className="text-[10px] text-slate-400">click to trace</span></div>
          <div className="divide-y divide-slate-200">
            {keyFacts.map((f) => (
              <div key={f.key} className="flex items-center justify-between gap-2 px-4 py-2" data-testid={`fact-row-${f.key}`}>
                <span className="text-xs text-slate-600 shrink-0">{f.label}</span>
                <span className="flex items-center gap-2 min-w-0"><span className="num text-xs text-slate-900 truncate">{fmtVal(f.value, f.format)}</span><SourceBadge src={f} /></span>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-2 p-3 border-t border-slate-200">
            {["egi", "ads"].map((k) => <MetricCard key={k} m={M[k]} metrics={M} />)}
          </div>
        </div>
      </div>
    </div>
  );
}
